import {isNumber} from 'lodash-es';
import type {
	Gsoa_ShoppingList_GetItemsCollection as ShoppingListItem,
	Gsoa_ShoppingList_SyncShoppingListUpdateCollection as SyncUpdateItem,
} from '~/openapi';

function resolveFinalItemOrder<T extends {ID: number}>(items: T[], orderedItemIds: number[]): T[] {
	const itemsById = new Map(items.map((item) => [item.ID, item]));
	const ordered: T[] = [];

	for (const id of orderedItemIds) {
		const item = itemsById.get(id);
		if (item) {
			ordered.push(item);
			itemsById.delete(id);
		}
	}

	itemsById.forEach((item) => ordered.push(item));
	return ordered;
}

function getVisualOrderSortedIds(items: ShoppingListItem[]) {
	return [...items]
		.map((item, index) => ({item, index}))
		.sort((a, b) => {
			const aOrder = isNumber(a.item.VisualOrder) ? a.item.VisualOrder : Number.MAX_SAFE_INTEGER;
			const bOrder = isNumber(b.item.VisualOrder) ? b.item.VisualOrder : Number.MAX_SAFE_INTEGER;
			if (aOrder !== bOrder) {
				return aOrder - bOrder;
			}
			if (a.item.ID !== b.item.ID) {
				return a.item.ID - b.item.ID;
			}
			return a.index - b.index;
		})
		.map(({item}) => item.ID);
}

function toSyncUpdateItem(item: ShoppingListItem, visualOrder: number): SyncUpdateItem {
	return {
		Code: item.Code,
		Text: item.Text,
		VANR: item.VANR ?? undefined,
		Amount: item.Amount ?? undefined,
		Purchased: item.Purchased,
		GeneralizedProduct: item.GeneralizedProduct,
		VisualOrder: visualOrder,
	};
}

export function buildVisualOrderSyncPayload(
	items: ShoppingListItem[],
	orderedItemIds: number[],
): SyncUpdateItem[] {
	return resolveFinalItemOrder(items, orderedItemIds)
		.map((item, index) => toSyncUpdateItem(item, toVisualOrderPosition(index)))
		.sort((a, b) => (b.VisualOrder ?? 0) - (a.VisualOrder ?? 0));
}

export function needsVisualOrderSync(items: ShoppingListItem[], orderedItemIds: number[]): boolean {
	const targetIds = resolveFinalItemOrder(items, orderedItemIds).map((item) => item.ID);
	const currentIds = getVisualOrderSortedIds(items);

	return targetIds.length !== currentIds.length
		|| targetIds.some((id, index) => id !== currentIds[index]);
}

function toVisualOrderPosition(index: number) {
	return index + 1;
}
